import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import ReactLoading from 'react-loading';
import { Services } from 'Services';
import { Success, Error } from 'Util/Toast';
import Layout from 'Components/Layout';
import { Helmet } from 'react-helmet';
import { BsCloudUpload } from 'react-icons/bs';
import CommonTutorClasses from '../commoncssfiles/CategoryTutor.module.css';
import UserClasses from '../users/Users.module.css';
import CategoryClasses from '../categories/AddCategory.module.css';
import ProductClasses from '../products/Products.module.css';

const EditBanner = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { register, handleSubmit, setValue, formState: { errors } } = useForm();
    const [imagePreview, setImagePreview] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState('');

    // Retrieve the category object from location state
    const { category } = location.state || {};

    // Use useEffect to prefill form fields and image preview when category changes
    useEffect(() => {
        if (category) {
            setValue('bannerName', category.bannerName);
            setValue('priority', category.priority);
            if (category.imageUrl) {
                setImagePreview(category.imagePreview);
                const endpoint = category.imageUrl.split('/').pop();
                setUploadedFileName(endpoint);
                setValue('imageUrl', endpoint);
            }
        }
    }, [category, setValue]);

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
            Error(`${file.name} is not a valid image format.`);
            return;
        }

        let formData = new FormData();
        formData.append("image", file);
        formData.append("folder", "banners");

        try {
            setLoading(true);
            const res = await Services.UploadImage(formData);
            if (res?.Status === 1) {
                const uploadedUrl = res?.ImageURL;
                setUploadedFileName(res?.ImageID);
                setImagePreview(uploadedUrl || URL.createObjectURL(file));
                setValue('imageUrl', res?.ImageID);
                setLoading(false);
            } else {
                Error(res?.Message || "Upload failed.");
                setLoading(false);
            }
        } catch (err) {
            console.error("Upload Error:", err);
            Error("Upload error. Please try again.");
            setLoading(false);
        }
    };

    const onSubmit = async (data) => {
        console.log('Form submitted with data:', data);
        console.log('Uploaded file name:', uploadedFileName);
        
        if (!uploadedFileName) {
            Error('Please upload an image.');
            return;
        }

        setLoading(true);

        const requestBody = {
            bannerName: data.bannerName,
            imageUrl: uploadedFileName,
            priority: parseInt(data.priority),
        };

        console.log('Request body:', requestBody);

        const token = { Authorization: `token ${localStorage.getItem('Token')}` };

        try {
            const response = await Services.updateBanner(category.bannerId, requestBody, token);
            console.log('API Response:', response);
            
            if (response.Status === 1) {
                navigate('/banners');
                Success(response.Message);
                console.log('Banner updated successfully:', response.Message);
            } else {
                Error(response.Message);
                console.error('Error updating banner:', response.Message);
            }
        } catch (error) {
            console.error('Error updating banner:', error);
            Error('An error occurred while updating the banner.');
        } finally {
            setLoading(false);
        }
    };

    const fileuploadDesign = (
        <div className={CommonTutorClasses["add-item"]} onClick={() => document.getElementById("image").click()}>
            <div className={CommonTutorClasses['upload-image']}>
                {imagePreview && <img src={imagePreview} alt="Selected" className={CommonTutorClasses['uploaded-coupon-image']} />}
                {loading && <ReactLoading className={ProductClasses['loading-spinner-container']} type="spin" color="#000" height={50} width={50} />}
                {!imagePreview && (
                    <div className={CommonTutorClasses['add-item']}>
                        <BsCloudUpload className={CommonTutorClasses['upload-icon']} />
                        <h5>Drag Your Files here</h5>
                        <p>(Only *.jpeg, *.png, *.webp will be accepted)</p>
                    </div>
                )}
                <input type="file" id="image" onChange={handleImageUpload} style={{ display: 'none' }} />
            </div>
        </div>
    );

    return (
        <Layout Active={'Banners'}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Update Banner</title>
            </Helmet>
            <div className={UserClasses['main-first-container']}>
                <div className={UserClasses['main-data-tab']}>
                    <div className={UserClasses['add-users-btn']}>
                        <Button variant="contained" color="primary" onClick={() => navigate(-1)}>
                            BACK
                        </Button>
                    </div>
                    <div className={CommonTutorClasses['wrapper']} data-aos="zoom-in-down">
                        <h2 className={CommonTutorClasses['main-header']}>Update Banner</h2>
                        <form style={{display: 'block'}} className={CommonTutorClasses["form"]} onSubmit={handleSubmit(onSubmit)}>
                            <div style={{marginBottom: '10px'}} className={CommonTutorClasses['form-control']}>
                                <label htmlFor="bannerName">Banner Name</label>
                                <input 
                                    style={{width: '97%'}} 
                                    type='text' 
                                    id="bannerName"
                                    {...register('bannerName', { required: 'Banner name is required' })} 
                                />
                                {errors.bannerName && <p className={CategoryClasses["error-text"]}>{errors.bannerName.message}</p>}
                            </div>
                            <div style={{marginBottom: '10px'}} className={CommonTutorClasses['form-control']}>
                                <label htmlFor="priority">Priority</label>
                                <input
                                    style={{width: '97%'}}
                                    type='number'
                                    id="priority"
                                    {...register('priority', { required: 'Priority is required', valueAsNumber: true })}
                                />
                                {errors.priority && <p className={CategoryClasses["error-text"]}>{errors.priority.message}</p>}
                            </div>
                            <div className={CommonTutorClasses['file-upload-wrapper']}>
                                <header>
                                    <h3>
                                        Upload Banner Image <span className="important">*</span>
                                    </h3>
                                </header>
                                <div className={CommonTutorClasses["upload-image"]}>
                                    {fileuploadDesign}
                                </div>
                                {!uploadedFileName && <p className="error-message">Please upload a Banner image.</p>}
                            </div>
                            <div className={ProductClasses['form-btn-main']}>
                                <Button 
                                    style={{marginTop: '10px'}} 
                                    type="submit" 
                                    variant="contained"
                                    disabled={loading}
                                    onClick={(e) => {
                                        console.log('Button clicked');
                                        // Let the form handle the submission
                                    }}
                                >
                                    {loading ? 'Updating Banner...' : 'Update Banner'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default EditBanner;